var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assertExists } from '@blocksuite/global/utils';
import { INLINE_ROOT_ATTR, ZERO_WIDTH_NON_JOINER, ZERO_WIDTH_SPACE, } from '@blocksuite/inline';
import { ShadowlessElement, WithDisposable } from '@blocksuite/lit';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FontLinkedPageIcon, FontPageIcon } from '../../../../icons/index.js';
import { getClosestBlockElementByElement, getModelByElement, getPageByElement, } from '../../../../utils/index.js';
import { DEFAULT_PAGE_NAME, REFERENCE_NODE } from '../../consts.js';
import { affineTextStyles } from './affine-text.js';
let AffineReference = class AffineReference extends WithDisposable(ShadowlessElement) {
    constructor() {
        super(...arguments);
        this.delta = {
            insert: ZERO_WIDTH_SPACE,
            attributes: {},
        };
        this.selected = false;
        this._refAttribute = {
            type: 'LinkedPage',
            pageId: '0',
        };
        this._updateRefMeta = (page) => {
            const refAttribute = this.delta.attributes?.reference;
            assertExists(refAttribute, 'Failed to get reference attribute!');
            this._refAttribute = refAttribute;
            const refMeta = page.workspace.meta.pageMetas.find(page => page.id === refAttribute.pageId);
            this._refMeta = refMeta
                ? {
                    ...refMeta,
                }
                : undefined;
        };
    }
    get inlineRoot() {
        const inlineRoot = this.closest(`[${INLINE_ROOT_ATTR}]`);
        assertExists(inlineRoot);
        return inlineRoot;
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.delta.insert !== REFERENCE_NODE) {
            console.error(`Reference node must be initialized with '${REFERENCE_NODE}', but got '${this.delta.insert}'`);
        }
        const closestBlock = getClosestBlockElementByElement(this);
        if (!closestBlock)
            return;
        const page = closestBlock.page;
        this._updateRefMeta(page);
        this._disposables.add(page.workspace.slots.pagesUpdated.on(() => this._updateRefMeta(page)));
        // observe yText update
        this.disposables.add(this.inlineRoot.inlineEditor.slots.textChange.on(() => this._updateRefMeta(page)));
    }
    _onClick() {
        const refMeta = this._refMeta;
        const model = getModelByElement(this);
        if (!refMeta) {
            // The page is deleted
            console.warn('The page is deleted', this._refAttribute.pageId);
            return;
        }
        if (refMeta.id === model.page.id) {
            // the page is the current page.
            return;
        }
        const targetPageId = refMeta.id;
        const root = model.page.root;
        assertExists(root);
        const pageElement = getPageByElement(this);
        assertExists(pageElement);
        pageElement.slots.pageLinkClicked.emit({ pageId: targetPageId });
    }
    render() {
        const refMeta = this._refMeta;
        const unavailable = !refMeta;
        const title = unavailable
            ? // Maybe the page is deleted
                'Deleted page'
            : refMeta.title;
        const attributes = this.delta.attributes;
        assertExists(attributes, 'Failed to get attributes!');
        const type = attributes.reference?.type;
        assertExists(type, 'Unable to get reference type!');
        const style = affineTextStyles(attributes, unavailable
            ? {
                color: 'var(--affine-text-disable-color)',
                textDecoration: 'line-through',
                fill: 'var(--affine-text-disable-color)',
            }
            : {});
        // we need to add `<v-text .str=${ZERO_WIDTH_NON_JOINER}></v-text>` in an
        // embed element to make sure inline range calculation is correct
        return html `<span
      data-selected=${this.selected}
      class="affine-reference"
      style=${style}
      @click=${this._onClick}
      >${type === 'LinkedPage' ? FontLinkedPageIcon : FontPageIcon}<span
        data-title=${title || DEFAULT_PAGE_NAME}
        class="affine-reference-title"
        >${title || DEFAULT_PAGE_NAME}</span
      ><v-text .str=${ZERO_WIDTH_NON_JOINER}></v-text
    ></span>`;
    }
};
AffineReference.styles = css `
    .affine-reference {
      white-space: nowrap;
      word-break: break-word;
      color: var(--affine-text-primary-color);
      fill: var(--affine-icon-color);
      border-radius: 4px;
      text-decoration: none;
      cursor: pointer;
      user-select: none;
      padding: 1px 2px 1px 0;
    }
    .affine-reference:hover {
      background: var(--affine-hover-color);
    }

    .affine-reference[data-selected='true'] {
      background: var(--affine-hover-color);
    }

    .affine-reference-title {
      margin-left: 4px;
      border-bottom: 0.5px solid var(--affine-divider-color);
      transition: border 0.2s ease-out;
    }
    .affine-reference-title:hover {
      border-bottom: 0.5px solid var(--affine-icon-color);
    }

    .affine-reference > span {
      white-space: break-spaces;
    }
  `;
__decorate([
    property({ type: Object })
], AffineReference.prototype, "delta", void 0);
__decorate([
    property({ type: Boolean })
], AffineReference.prototype, "selected", void 0);
__decorate([
    state()
], AffineReference.prototype, "_refMeta", void 0);
AffineReference = __decorate([
    customElement('affine-reference')
], AffineReference);
export { AffineReference };
//# sourceMappingURL=reference-node.js.map